import styles from './styles.module.css'

import { SegmentedControl } from '@radix-ui/themes'
import React, { useEffect, useRef, useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import { getConstanceSettings } from '@disco/state-management/selectors/user'

import { Brief } from 'types/brief'

import { Box, Dialog, Text } from 'components/ds'

import { useSelector } from 'hooks/use-redux'

import { AssignUrlTab, AssignUrlTabState } from './assign-url-tab'
import { BriefCard } from './brief-card'
import { EmailTab, EmailTabState } from './email-tab'

interface ShareBriefModalProps {
  onClose?: () => void
  brief: Brief
  initialRecipients?: string[]
  onManageAccess: () => void
  handleReopenBrief: () => Promise<void>
  allRecipients?: Array<{ email: string; contactId?: number }>
}

type TabValue = 'email' | 'assign-url'

export const ShareBriefModal: React.FC<ShareBriefModalProps> = ({
  onClose,
  brief,
  initialRecipients = [],
  onManageAccess,
  handleReopenBrief,
  allRecipients,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>('email')
  const emailStateRef = useRef<EmailTabState | null>(null)
  const assignStateRef = useRef<AssignUrlTabState | null>(null)

  const constanceSettings = useSelector(getConstanceSettings)
  const briefWorkflowV2Enabled = constanceSettings?.BRIEF_WORKFLOW_V2 || false

  const isDraft = briefWorkflowV2Enabled && brief.publishing_status === 'draft'

  useEffect(() => {
    mixpanel.sendPureEvent('Brief share modal opened', {
      Context: 'Briefs',
      brief_id: brief.id,
    })
  }, [brief.id])

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue)
  }

  return (
    <Dialog.Root defaultOpen onOpenChange={onClose}>
      <Dialog.Content
        maxWidth="600px"
        align="start"
        aria-describedby={undefined}
        className={styles.dialogContent}
        onEscapeKeyDown={event => {
          // Let an open combobox (RecipientInput dropdown) handle Escape first
          // instead of closing the modal — matches native ARIA combobox behavior.
          const activeEl = document.activeElement
          if (
            activeEl?.getAttribute('role') === 'combobox' &&
            activeEl.getAttribute('aria-expanded') === 'true'
          ) {
            event.preventDefault()
          }
        }}
      >
        <Dialog.Title>
          <Text size="6" weight="bold" mb="3" as="div">
            Share brief
          </Text>
        </Dialog.Title>

        <Box mb="4">
          <BriefCard brief={brief} />
        </Box>

        {isDraft ? (
          <Box p="3" className={styles.draftNotice}>
            <Text size="2" color="orange" weight="medium">
              This brief is still in draft and cannot be shared. You need to
              publish the brief first to share it.
            </Text>
          </Box>
        ) : (
          <>
            <Box mb="4">
              <SegmentedControl.Root
                value={activeTab}
                onValueChange={handleTabChange}
                className={styles.fullWidth}
              >
                <SegmentedControl.Item
                  value="email"
                  className={styles.focusable}
                >
                  Email brief
                </SegmentedControl.Item>
                <SegmentedControl.Item
                  value="assign-url"
                  className={styles.focusable}
                >
                  Assign a URL
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </Box>

            <div className={styles.tabBody}>
              {activeTab === 'email' ? (
                <EmailTab
                  brief={brief}
                  initialRecipients={initialRecipients}
                  allRecipients={allRecipients}
                  onManageAccess={onManageAccess}
                  handleReopenBrief={handleReopenBrief}
                  onClose={onClose}
                  persistedStateRef={emailStateRef}
                  briefWorkflowV2Enabled={briefWorkflowV2Enabled}
                />
              ) : (
                <AssignUrlTab
                  brief={brief}
                  onClose={onClose}
                  onManageAccess={onManageAccess}
                  persistedStateRef={assignStateRef}
                />
              )}
            </div>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}
